// Alta manual de una reserva (venta por WhatsApp, teléfono o mostrador, sin Stripe).
//
// 🔒 SINGLE-SOURCE · ai-agents:read-before-modify — el ÚNICO formulario de alta
// manual. Lo usan:
//   · Sales Desk → «Reservas vendidas» (la vendedora registra SU venta), y
//   · Viajes → Reservaciones → «Nueva Reserva» (el admin; puede elegir vendedor).
// Antes el formulario vivía dentro de TravelAdmin y la vendedora no lo veía.
//
// Quién puede y a nombre de quién queda lo decide el SERVIDOR
// (`Company.vendedores_registran_ventas`, mcp_django #563): una vendedora solo
// registra si su org lo activó, la venta queda a su nombre y el precio sale del
// catálogo. Aquí no se calcula ningún monto: el total lo devuelve el servidor.

import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Button, ErrorNote } from '@/modules/orion-shell/ui';
import { createBookingManual, listPlans, listTrips, type Plan, type Trip } from '@/lib/api_travel';
import { listSellers, type OrgUser } from '../api';

const campo = 'w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50';
const rotulo = 'mb-1 block text-xs font-medium text-muted-foreground';

export function RegistrarVenta({ open, onClose, onCreated, isAdmin, trips: tripsDados }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** El admin elige a qué vendedor se le atribuye; a la vendedora se le atribuye sola. */
  isAdmin: boolean;
  /** Si el padre ya tiene los viajes (TravelAdmin), se reusan. */
  trips?: Trip[];
}) {
  const [trips, setTrips] = useState<Trip[]>(tripsDados ?? []);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [sellers, setSellers] = useState<OrgUser[]>([]);
  const [tripId, setTripId] = useState('');
  const [planId, setPlanId] = useState('');
  const [fecha, setFecha] = useState('');
  const [adultos, setAdultos] = useState('1');
  const [ninos, setNinos] = useState('0');
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [correo, setCorreo] = useState('');
  const [anticipo, setAnticipo] = useState('');
  const [notas, setNotas] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hecha, setHecha] = useState<{ codigo: string; total: string } | null>(null);

  useEffect(() => { if (tripsDados) setTrips(tripsDados); }, [tripsDados]);

  useEffect(() => {
    if (!open) return;
    if (!tripsDados) listTrips(200).then(r => setTrips(r.items ?? [])).catch(() => setTrips([]));
    if (isAdmin) listSellers().then(setSellers).catch(() => setSellers([]));
  }, [open, isAdmin, tripsDados]);

  // Al elegir la experiencia, sus planes (el precio sale del catálogo, no se teclea).
  useEffect(() => {
    if (!tripId) { setPlans([]); setPlanId(''); return; }
    listPlans(Number(tripId))
      .then(r => { const ps = (r as any).items ?? r ?? []; setPlans(ps); setPlanId(ps[0] ? String(ps[0].id) : ''); })
      .catch(() => setPlans([]));
  }, [tripId]);

  const limpiar = () => {
    setTripId(''); setPlanId(''); setFecha(''); setAdultos('1'); setNinos('0'); setNombre('');
    setTelefono(''); setCorreo(''); setAnticipo(''); setNotas(''); setVendedor(''); setError(null); setHecha(null);
  };
  const cerrar = () => { limpiar(); onClose(); };

  const guardar = async () => {
    setError(null);
    const a = parseInt(adultos, 10) || 0;
    const n = parseInt(ninos, 10) || 0;
    if (!tripId || !planId) return setError('Elige la experiencia y el plan.');
    if (!fecha) return setError('Falta la fecha.');
    if (!nombre.trim() || !telefono.trim()) return setError('Falta el nombre o el teléfono del cliente.');
    if (a + n < 1) return setError('Indica al menos una persona.');
    setGuardando(true);
    try {
      const r = await createBookingManual({
        trip_id: Number(tripId),
        plan_id: Number(planId),
        date_trip: fecha,
        customer_name: nombre.trim(),
        customer_phone: telefono.trim(),
        customer_email: correo.trim(),
        persons: [
          ...(a > 0 ? [{ person_type: 'adult', quantity: a }] : []),
          ...(n > 0 ? [{ person_type: 'child', quantity: n }] : []),
        ],
        amount_paid: anticipo ? parseFloat(anticipo) : 0,
        notes: notas.trim(),
        ...(isAdmin && vendedor ? { seller_user_id: vendedor } : {}),
      });
      setHecha({ codigo: (r as any).confirmation_code || `#${(r as any).id}`, total: String((r as any).total ?? '') });
      onCreated();
    } catch (e: any) {
      setError(e?.message || 'No se pudo registrar la venta.');
    } finally {
      setGuardando(false);
    }
  };

  const total = hecha?.total ? `$${Number(hecha.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '';

  return (
    <Sheet open={open} onClose={cerrar} title="Registrar venta" label="Registrar venta" inShell
      footer={hecha ? (
        <div className="flex w-full gap-2">
          <Button variant="secondary" onClick={limpiar} className="flex-1 py-3">Registrar otra</Button>
          <Button variant="brand" onClick={cerrar} className="flex-1 py-3">Listo</Button>
        </div>
      ) : (
        <Button variant="brand" onClick={guardar} disabled={guardando} className="w-full py-3">
          {guardando ? 'Guardando…' : 'Registrar venta'}
        </Button>
      )}>
      {hecha ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center" role="status">
          <CheckCircle2 size={44} className="text-emerald-500" aria-hidden />
          <p className="text-lg font-semibold text-foreground">Venta registrada</p>
          <p className="text-sm text-muted-foreground">
            Código <span className="font-mono text-foreground">{hecha.codigo}</span>{total && <> · total {total}</>}
          </p>
          <p className="max-w-xs text-xs text-muted-foreground">
            {isAdmin ? 'Ya aparece en Reservaciones y en Ventas.' : 'Ya aparece en tus ventas, a tu nombre.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs leading-relaxed text-muted-foreground">
            Para ventas por WhatsApp, teléfono o mostrador. El precio se toma del catálogo.
          </p>
          <div>
            <label htmlFor="rv-exp" className={rotulo}>Experiencia</label>
            <select id="rv-exp" value={tripId} onChange={e => setTripId(e.target.value)} className={campo}>
              <option value="">Elegir experiencia…</option>
              {trips.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="rv-plan" className={rotulo}>Plan</label>
            <select id="rv-plan" value={planId} onChange={e => setPlanId(e.target.value)} disabled={!plans.length} className={campo}>
              {!plans.length && <option value="">Elige una experiencia primero</option>}
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="rv-fecha" className={rotulo}>Fecha</label>
              <input id="rv-fecha" type="date" value={fecha} onChange={e => setFecha(e.target.value)} className={campo} />
            </div>
            <div>
              <label htmlFor="rv-adultos" className={rotulo}>Adultos</label>
              <input id="rv-adultos" type="number" inputMode="numeric" min={0} value={adultos} onChange={e => setAdultos(e.target.value)} className={campo} />
            </div>
            <div>
              <label htmlFor="rv-ninos" className={rotulo}>Niños</label>
              <input id="rv-ninos" type="number" inputMode="numeric" min={0} value={ninos} onChange={e => setNinos(e.target.value)} className={campo} />
            </div>
          </div>
          <div>
            <label htmlFor="rv-nombre" className={rotulo}>Nombre del cliente</label>
            <input id="rv-nombre" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre completo" autoComplete="off" className={campo} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="rv-tel" className={rotulo}>Teléfono</label>
              <input id="rv-tel" type="tel" inputMode="tel" value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="10 dígitos" className={campo} />
            </div>
            <div>
              <label htmlFor="rv-correo" className={rotulo}>Correo (opcional)</label>
              <input id="rv-correo" type="email" value={correo} onChange={e => setCorreo(e.target.value)} placeholder="correo@ejemplo.com" className={campo} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="rv-anticipo" className={rotulo}>Anticipo cobrado (opcional)</label>
              <input id="rv-anticipo" type="number" inputMode="decimal" min={0} value={anticipo} onChange={e => setAnticipo(e.target.value)} placeholder="0.00" className={campo} />
            </div>
            <div>
              <label htmlFor="rv-notas" className={rotulo}>Notas (opcional)</label>
              <input id="rv-notas" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Punto de encuentro, hora…" className={campo} />
            </div>
          </div>
          {isAdmin && sellers.length > 0 && (
            <div>
              <label htmlFor="rv-vendedor" className={rotulo}>Vendedor (opcional)</label>
              <select id="rv-vendedor" value={vendedor} onChange={e => setVendedor(e.target.value)} className={campo}>
                <option value="">Sin vendedor (venta de la casa)</option>
                {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.full_name || s.username}</option>)}
              </select>
            </div>
          )}
          {error && <ErrorNote>{error}</ErrorNote>}
        </div>
      )}
    </Sheet>
  );
}
